<template>
	<v-avatar
		:size="size"
		:class="avatarClasses"
		:color="backgroundColorComputed"
	>
		<v-icon
			v-if="icon"
			:icon="icon"
			:color="iconColorComputed"
			:size="iconSizeComputed"
		/>
		<span v-else-if="text" :class="textClasses" :style="textStyle">
			{{ textComputed }}
		</span>
		<v-icon
			v-else
			:icon="fallbackIcon"
			:color="iconColorComputed"
			:size="iconSizeComputed"
		/>
	</v-avatar>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Props
interface Props {
	/** Ícone a ser exibido */
	icon?: string;
	/** Texto a ser exibido (alternativo ao ícone) */
	text?: string;
	/** Cor de fundo do avatar */
	backgroundColor?: string;
	/** Cor customizada do ícone/texto (sobrescreve cálculo automático) */
	iconColor?: string;
	/** Tamanho do avatar */
	size?: string | number;
	/** Ícone de fallback quando não há ícone nem texto */
	fallbackIcon?: string;
	/** Classes CSS adicionais para o avatar */
	class?: string | string[] | object;
	/** Usar apenas a primeira letra do texto */
	firstLetterOnly?: boolean;
	/** Densidade do avatar (afeta padding interno) */
	density?: "default" | "comfortable" | "compact";
}

const props = withDefaults(defineProps<Props>(), {
	fallbackIcon: "mdi-star-four-points",
	size: 32,
	firstLetterOnly: true,
	density: "default",
});

// Computed
const backgroundColorComputed = computed(() => {
	return props.backgroundColor || "grey-lighten-2";
});

const iconColorComputed = computed(() => {
	// Se foi fornecida uma cor customizada, usar ela
	if (props.iconColor) {
		return props.iconColor;
	}

	// Se não há cor de fundo definida, usar cor padrão
	if (!props.backgroundColor) {
		return "grey-darken-2";
	}

	// Calcular cor baseada na luminância da cor de fundo
	return getIconColor(props.backgroundColor);
});

const iconSizeComputed = computed(() => {
	const avatarSize =
		typeof props.size === "string" ? parseInt(props.size) : props.size;

	// Calcular tamanho do ícone baseado no tamanho do avatar
	if (avatarSize <= 24) return "x-small";
	if (avatarSize <= 32) return "small";
	if (avatarSize <= 40) return "default";
	return "large";
});

const textComputed = computed(() => {
	if (!props.text) return "";

	if (props.firstLetterOnly) {
		return props.text.charAt(0).toUpperCase();
	}

	return props.text;
});

const avatarClasses = computed(() => {
	const classes = [];

	if (props.class) {
		if (typeof props.class === "string") {
			classes.push(props.class);
		} else if (Array.isArray(props.class)) {
			classes.push(...props.class);
		} else {
			// Objeto de classes condicionais
			Object.entries(props.class).forEach(([className, condition]) => {
				if (condition) {
					classes.push(className);
				}
			});
		}
	}

	return classes;
});

const textClasses = computed(() => {
	const avatarSize =
		typeof props.size === "string" ? parseInt(props.size) : props.size;

	// Definir classes de texto baseadas no tamanho
	if (avatarSize <= 20) return "text-caption";
	if (avatarSize <= 28) return "text-body-2";
	if (avatarSize <= 36) return "text-body-1";
	return "text-h6";
});

const textStyle = computed(() => {
	const avatarSize =
		typeof props.size === "string" ? parseInt(props.size) : props.size;

	// Para avatars muito pequenos, ajustar font-size manualmente
	if (avatarSize <= 16) {
		return {
			fontSize: "8px",
			fontWeight: "500",
			color: iconColorComputed.value,
		};
	}

	if (avatarSize <= 20) {
		return {
			fontSize: "10px",
			fontWeight: "500",
			color: iconColorComputed.value,
		};
	}

	return {
		fontWeight: "500",
		color: iconColorComputed.value,
	};
});

// Methods
const getIconColor = (backgroundColor: string): string => {
	if (!backgroundColor) return "grey-darken-2";

	// Tratar cores do Vuetify (ex: 'primary', 'success', etc.)
	if (!backgroundColor.startsWith("#") && !backgroundColor.startsWith("rgb")) {
		// Para cores do Vuetify, usar contraste padrão
		const lightColors = ["warning", "lime", "yellow", "amber", "orange"];
		const lightGreyColors = [
			"grey-lighten-5",
			"grey-lighten-4",
			"grey-lighten-3",
			"grey-lighten-2",
			"grey-lighten-1",
		];

		if (
			lightColors.some((color) => backgroundColor.includes(color)) ||
			lightGreyColors.some((color) => backgroundColor.includes(color))
		) {
			return "grey-darken-2";
		}

		return "white";
	}

	// Converter hex para RGB e calcular luminância
	let hex = backgroundColor;
	if (hex.startsWith("#")) {
		hex = hex.replace("#", "");
	} else if (hex.startsWith("rgb")) {
		// Extrair valores RGB de string rgb(r, g, b)
		const rgbMatch = hex.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
		if (rgbMatch) {
			const r = parseInt(rgbMatch[1]);
			const g = parseInt(rgbMatch[2]);
			const b = parseInt(rgbMatch[3]);
			const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
			return luminance > 0.5 ? "grey-darken-2" : "white";
		}
		return "grey-darken-2";
	}

	// Para hex, garantir que tem 6 caracteres
	if (hex.length === 3) {
		hex = hex
			.split("")
			.map((char) => char + char)
			.join("");
	}

	if (hex.length !== 6) {
		return "grey-darken-2";
	}

	const r = parseInt(hex.substr(0, 2), 16);
	const g = parseInt(hex.substr(2, 2), 16);
	const b = parseInt(hex.substr(4, 2), 16);

	// Calcular luminância relativa
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// Se a cor de fundo for clara, usar ícone escuro, senão usar claro
	return luminance > 0.5 ? "grey-darken-2" : "white";
};
</script>

<style scoped>
.v-avatar {
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
